import { useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { skillManagementApi } from "../services/api";
import { useAgentPermissions } from "../context/AgentPermissionsContext";
import { skillDomainLabel as domainLabel } from "./skillVocabulary";

// One refusal reason, one sentence. Every sentence ends the same way because every refusal
// mutates nothing. An unknown reason falls back to the raw token instead of a blank message.
const REFUSAL_LABELS = {
  unsupported_domain: "התחום אינו בווקבולרי המוסמך (§28) — לא בוצע שינוי.",
  no_operator_identity: "לא זוהתה זהות מפעיל מאומתת — לא בוצע שינוי.",
  operator_is_the_employee: "המפעיל הוא העובד עצמו (§28.10 — אין אישור עצמי) — לא בוצע שינוי.",
  unknown_skill: "לא נמצאה רשומת מיומנות — לא בוצע שינוי.",
  invalid_transition: "הרשומה כבר במצב סופי (deprecated) ואינה ניתנת להסרה חוזרת — לא בוצע שינוי.",
  not_assigned_to_employee: "הרשומה אינה משויכת לעובד הזה — לא בוצע שינוי.",
};

const STATE_META = {
  applied: { cls: "text-green-700 bg-green-50 border-green-200", role: "status" },
  already_present: { cls: "text-gray-600 bg-gray-50 border-gray-200", role: "status" },
  refused: { cls: "text-amber-700 bg-amber-50 border-amber-200", role: "alert" },
};

function normalizeContract(raw) {
  const entries = Array.isArray(raw?.domains) ? raw.domains : null;
  if (entries === null) return null;
  const seen = new Set();
  const domains = [];
  for (const entry of entries) {
    const token = typeof entry?.domain === "string" ? entry.domain.trim() : "";
    if (!token || seen.has(token)) continue;
    seen.add(token);
    domains.push({
      domain: token,
      subdomains: Array.isArray(entry?.subdomains)
        ? entry.subdomains.filter((sub) => typeof sub === "string" && sub.trim())
        : [],
    });
  }
  return domains;
}

function outcomeSentence(verb, outcome) {
  if (outcome?.state === "refused") {
    const reason = typeof outcome.reason === "string" ? outcome.reason : "";
    return REFUSAL_LABELS[reason] || `הבקשה נדחתה (${reason || "ללא סיבה מדווחת"}) — לא בוצע שינוי.`;
  }
  if (outcome?.state === "already_present") {
    return `העובד כבר מחזיק בתחום ${domainLabel(outcome.domain)} — לא נוצרה רשומה חדשה.`;
  }
  const level = Number.isInteger(outcome?.current_level) ? outcome.current_level : null;
  const version = Number.isInteger(outcome?.version) ? outcome.version : null;
  const facts = [
    version !== null ? `גרסה ${version}` : "גרסה לא דווחה",
    level !== null ? `רמה ${level}` : "רמה לא דווחה",
  ].join(", ");
  if (verb === "unassign") {
    return `השיוך הוסר: ${domainLabel(outcome?.domain)} סומן כ-deprecated והרשומה נשמרת עם ההיסטוריה שלה (${facts}).`;
  }
  return `הוקצתה מיומנות: ${domainLabel(outcome?.domain)} (${facts}).`;
}

export default function SkillManagementPanel({ agentId, records = [] }) {
  const qc = useQueryClient();
  const { can } = useAgentPermissions();
  const canManage = can("agents_manage");

  const [selectedDomain, setSelectedDomain] = useState("");
  const [confirmingSkillId, setConfirmingSkillId] = useState(null);
  const [outcome, setOutcome] = useState(null);

  const contractQuery = useQuery({
    queryKey: ["skill-management-contract"],
    queryFn: () => skillManagementApi.contract(),
    enabled: canManage,
    retry: false,
    staleTime: 5 * 60_000,
  });

  const contract = useMemo(() => normalizeContract(contractQuery.data), [contractQuery.data]);

  const invalidateProjections = () => {
    for (const key of [
      "agent",
      "agent-workspace",
      "agent-growth-profile",
      "agent-experience-history",
      "skill-lifecycle",
    ]) {
      qc.invalidateQueries({ queryKey: [key] });
    }
  };

  const assignMutation = useMutation({
    mutationFn: (domain) => skillManagementApi.assign(agentId, domain),
    onSuccess: (result) => {
      setOutcome({ verb: "assign", result });
      setSelectedDomain("");
      invalidateProjections();
    },
  });

  const unassignMutation = useMutation({
    mutationFn: (skillId) => skillManagementApi.unassign(agentId, skillId),
    onSuccess: (result) => {
      setOutcome({ verb: "unassign", result });
      setConfirmingSkillId(null);
      invalidateProjections();
    },
  });

  if (!canManage) return null;

  const removable = records.filter(
    (record) => record?.skillId && record.statusKey !== "deprecated"
  );
  const busy = assignMutation.isPending || unassignMutation.isPending;
  const outcomeMeta = outcome ? STATE_META[outcome.result?.state] : null;

  return (
    <section
      aria-label="ניהול שיוך מיומנויות"
      data-testid="skill-management-panel"
      className="bg-white rounded-xl border shadow-sm p-4 space-y-3"
    >
      <div>
        <h2 className="text-sm font-semibold text-gray-700">ניהול שיוך מיומנויות</h2>
        <p className="text-[11px] text-gray-400 mt-0.5">
          פעולות מנוהלות בשרת ובזהות המפעיל המחובר. הקצאה יוצרת רשומת candidate ברמה 0 ובגרסה 1;
          הסרה מסמנת את הרשומה כ-deprecated ומשאירה אותה ואת ההיסטוריה שלה. אין כאן העלאת רמה,
          הענקת כלי, הרשאה או אישור — רמה עולה רק בהערכה אנושית מבוקרת (§28.5).
        </p>
      </div>

      <div className="space-y-1.5">
        <p className="text-xs text-gray-500 font-medium">הקצאת תחום מוסמך</p>
        {contractQuery.isLoading ? (
          <p className="text-[11px] text-gray-400 italic">טוען את רשימת התחומים המוסמכים...</p>
        ) : contractQuery.isError ? (
          <p
            role="alert"
            className="text-[11px] text-amber-700 bg-amber-50 border border-amber-200 rounded px-1.5 py-0.5"
          >
            רשימת התחומים המוסמכים לא נטענה — לא הוצעה הקצאה.
          </p>
        ) : contract === null ? (
          <p
            role="alert"
            className="text-[11px] text-amber-700 bg-amber-50 border border-amber-200 rounded px-1.5 py-0.5"
          >
            השרת לא דיווח רשימת תחומים מוסמכים — לא הוצעה הקצאה.
          </p>
        ) : contract.length === 0 ? (
          <p className="text-[11px] text-gray-500">
            השרת דיווח שאין תחומים מוסמכים להקצאה — לא הוצעה הקצאה.
          </p>
        ) : (
          <div className="flex flex-wrap items-center gap-1.5">
            <select
              data-testid="skill-management-domain-select"
              aria-label="תחום מיומנות להקצאה"
              value={selectedDomain}
              disabled={busy}
              onChange={(event) => setSelectedDomain(event.target.value)}
              className="text-xs border border-gray-300 rounded px-2 py-1 bg-white disabled:opacity-50"
            >
              <option value="">בחרו תחום מוסמך…</option>
              {contract.map((entry) => (
                <option key={entry.domain} value={entry.domain}>
                  {domainLabel(entry.domain)}
                  {entry.subdomains.length > 0
                    ? ` (כולל ${entry.subdomains.map(domainLabel).join(", ")})`
                    : ""}
                </option>
              ))}
            </select>
            <button
              type="button"
              data-testid="skill-management-assign"
              disabled={busy || !selectedDomain}
              onClick={() => {
                setOutcome(null);
                assignMutation.mutate(selectedDomain);
              }}
              className="text-[11px] px-2 py-1 rounded border border-brand-300 text-brand-700 bg-white hover:bg-brand-50 disabled:opacity-50"
            >
              הקצה מיומנות
            </button>
          </div>
        )}
        <p className="text-[10px] text-gray-400">
          ההקצאה היא ברמת התחום. תת-התחומים מוצגים לקריאה בלבד ואינם נבחרים כאן.
        </p>
      </div>

      <div className="space-y-1.5 border-t border-gray-100 pt-2">
        <p className="text-xs text-gray-500 font-medium">הסרת שיוך</p>
        {removable.length === 0 ? (
          <p className="text-[11px] text-gray-400 italic">אין רשומות משויכות להסרה.</p>
        ) : (
          <ul className="space-y-1">
            {removable.map((record) => (
              <li
                key={record.skillId}
                data-testid={`skill-management-assignment-${record.skillId}`}
                className="flex flex-wrap items-center gap-2 text-xs"
              >
                <span className="text-gray-700">{record.name || record.domain}</span>
                {Number.isInteger(record.version) && (
                  <span className="text-[10px] text-gray-400">גרסה {record.version}</span>
                )}
                {Number.isInteger(record.level) && (
                  <span className="text-[10px] text-gray-400">רמה {record.level}</span>
                )}
                {confirmingSkillId === record.skillId ? (
                  <span className="flex flex-wrap items-center gap-1.5 mr-auto">
                    <span className="text-[10px] text-amber-700">
                      ההסרה היא סופית (deprecated) ואינה מוחקת את הרשומה. לאשר?
                    </span>
                    <button
                      type="button"
                      data-testid={`skill-management-unassign-confirm-${record.skillId}`}
                      disabled={busy}
                      onClick={() => {
                        setOutcome(null);
                        unassignMutation.mutate(record.skillId);
                      }}
                      className="text-[10px] px-2 py-0.5 rounded border border-red-300 text-red-700 bg-white hover:bg-red-50 disabled:opacity-50"
                    >
                      אשר הסרה
                    </button>
                    <button
                      type="button"
                      disabled={busy}
                      onClick={() => setConfirmingSkillId(null)}
                      className="text-[10px] px-2 py-0.5 rounded border border-gray-300 text-gray-600 bg-white hover:bg-gray-50 disabled:opacity-50"
                    >
                      ביטול
                    </button>
                  </span>
                ) : (
                  <button
                    type="button"
                    data-testid={`skill-management-unassign-${record.skillId}`}
                    disabled={busy}
                    onClick={() => {
                      setOutcome(null);
                      setConfirmingSkillId(record.skillId);
                    }}
                    className="text-[10px] px-2 py-0.5 rounded border border-gray-300 text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 mr-auto"
                  >
                    הסר שיוך
                  </button>
                )}
              </li>
            ))}
          </ul>
        )}
        <p className="text-[10px] text-gray-400">
          ההסרה אינה מוחקת דבר: הרשומה, הרמה, הגרסה ויומן השינויים נשמרים, והמיומנות פשוט אינה
          ניתנת עוד לניתוב.
        </p>
      </div>

      {busy && <p className="text-[11px] text-gray-400 italic">שולח את הבקשה לשרת...</p>}

      {(assignMutation.isError || unassignMutation.isError) && (
        <p
          role="alert"
          className="text-[11px] text-red-600 bg-red-50 border border-red-200 rounded px-1.5 py-0.5"
        >
          הבקשה נכשלה ולא בוצע שינוי. אם השירות דיווח שהרישום הקבוע לא זמין — נסו שוב מאוחר יותר.
        </p>
      )}

      {outcome && !busy && outcomeMeta && (
        <p
          role={outcomeMeta.role}
          data-testid="skill-management-outcome"
          className={`text-[11px] border rounded px-1.5 py-0.5 ${outcomeMeta.cls}`}
        >
          {outcomeSentence(outcome.verb, outcome.result)}
        </p>
      )}
    </section>
  );
}
